import { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import OsFilterSelector from '../../../components/OsFilterSelector';
import AdpopcornDashboard from '../../../components/pointhome/adpopcorn/AdpopcornDashboard';
import AdpopcornTotalMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornTotalMetricsTable';
import AdpopcornMediationRevenueMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornMediationRevenueMetricsTable';
import AdpopcornMediationDetailMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornMediationDetailMetricsTable';
import AdpopcornPlacementMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornPlacementMetricsTable';
import apiClient from '../../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import {
  AdpopcornTotalMetrics,
  AdpopcornMediationRevenueMetrics,
  AdpopcornMediationDetailMetrics,
  AdpopcornPlacementMetrics
} from '../../../types/adpopcorn.types';

export default function LuckyLotteryAdPopcorn() {
  const [adpopcornTotalMetrics, setAdpopcornTotalMetrics] = useState<AdpopcornTotalMetrics | null>(null);
  const [adpopcornMediationRevenueMetrics, setAdpopcornMediationRevenueMetrics] = useState<AdpopcornMediationRevenueMetrics | null>(null);
  const [adpopcornMediationDetailMetrics, setAdpopcornMediationDetailMetrics] = useState<AdpopcornMediationDetailMetrics | null>(null);
  const [adpopcornPlacementMetrics, setAdpopcornPlacementMetrics] = useState<AdpopcornPlacementMetrics | null>(null);
  const [thirdPartyId, setThirdPartyId] = useState<number>(-1);
  const [thirdPartyName, setThirdPartyName] = useState<string>('미디에이션 전체');
  const [adpopcornTotalRevenue, setAdpopcornTotalRevenue] = useState<number>(0);
  const [adpopcornPreviousTotalRevenue, setAdpopcornPreviousTotalRevenue] = useState<number>(0);
  const [adpopcornDailyStats, setAdpopcornDailyStats] = useState<{
    date: string;
    revenue: number;
    req: number;
    res: number;
    imp: number;
    clk: number;
  }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const selectedApp = '앱 전체'; // 항상 앱 전체로 고정
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedOs, setSelectedOs] = useState<string>('all');

  useEffect(() => {
    setInitialized(true);
  }, []);

  const fetchAdpopcornTotalMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp }
        : {}),
      os_type: selectedOs,
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/total`, {
      params: params
    }).then((response) => {
      setAdpopcornTotalMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });

  }, [startDate, endDate, selectedApp, selectedOs]);

  const fetchAdpopcornMediationRevenueMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp }
        : {}),
      os_type: selectedOs,
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/mediation`, {
      params: params
    }).then((response) => {
      setAdpopcornMediationRevenueMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, selectedOs]);

  const fetchAdpopcornMediationDetailMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string | number | undefined> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp }
        : {}),
      os_type: selectedOs,
      ...(thirdPartyId !== -1 ? { third_party_id: thirdPartyId } : {})
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/mediation/detail`, {
      params: params
    }).then((response) => {
      setAdpopcornMediationDetailMetrics(response.data);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, selectedOs, thirdPartyId]);

  const fetchAdpopcornPlacementMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp }
        : {}),
      os_type: selectedOs,
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/placement`, {
      params: params
    }).then((response) => {
      setAdpopcornPlacementMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => {
    if (initialized && startDate && endDate) {
      if (activeTab === 0) {
        fetchAdpopcornTotalMetrics();
      } else if (activeTab === 1) {
        fetchAdpopcornMediationRevenueMetrics();
      }
      else if (activeTab === 2) {
        fetchAdpopcornMediationDetailMetrics();
      }
      else if (activeTab === 3) {
        fetchAdpopcornPlacementMetrics();
      }
    }
  }, [
    fetchAdpopcornTotalMetrics,
    fetchAdpopcornMediationRevenueMetrics,
    fetchAdpopcornMediationDetailMetrics,
    fetchAdpopcornPlacementMetrics,
    initialized, startDate, endDate, activeTab, thirdPartyId, selectedOs
  ]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box>
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={() => {
            if (startDate && endDate) {
              if (activeTab === 0) {
                fetchAdpopcornTotalMetrics();
              } else if (activeTab === 1) {
                fetchAdpopcornMediationRevenueMetrics();
              }
            }
          }}
        />
      </Box>

      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            <OsFilterSelector selectedOs={selectedOs} onChange={setSelectedOs} />

            <AdpopcornDashboard
              dailyStats={adpopcornDailyStats}
              totalRevenue={adpopcornTotalRevenue}
              previousTotalRevenue={adpopcornPreviousTotalRevenue}
            />

            <Tabs
              value={activeTab}
              onChange={(_, newValue) => {
                setActiveTab(newValue);
                if (newValue === 0) {
                  fetchAdpopcornTotalMetrics();
                } else if (newValue === 1) {
                  fetchAdpopcornMediationRevenueMetrics();
                }
              }}
              sx={{
                mt: 3
              }}
            >
              <Tab value={0} label="전체 현황" />
              <Tab value={1} label="미디에이션 매출" />
              <Tab value={2} label="미디에이션 상세 지표" />
              <Tab value={3} label="지면별 지표" />
            </Tabs>

            {
              activeTab === 0 && adpopcornTotalMetrics && startDate && endDate && (
                <AdpopcornTotalMetricsTable
                  data={adpopcornTotalMetrics}
                  showTopApps={true}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
            {
              activeTab === 1 && adpopcornMediationRevenueMetrics && startDate && endDate && (
                <AdpopcornMediationRevenueMetricsTable
                  data={adpopcornMediationRevenueMetrics}
                  selectedApp={selectedApp}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
            {
              activeTab === 2 && adpopcornMediationDetailMetrics && startDate && endDate && (
                <AdpopcornMediationDetailMetricsTable
                  data={adpopcornMediationDetailMetrics}
                  thirdPartyId={thirdPartyId}
                  thirdPartyName={thirdPartyName}
                  startDate={startDate}
                  endDate={endDate}
                  onThirdPartyIdChange={(id, name) => {
                    setThirdPartyId(id);
                    setThirdPartyName(name);
                  }}
                />
              )
            }
            {
              activeTab === 3 && adpopcornPlacementMetrics && startDate && endDate && (
                <AdpopcornPlacementMetricsTable
                  data={adpopcornPlacementMetrics}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
          </>
        )}
      </Box>
    </Box>
  );
}
